import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ArrowLeft, BookOpen, Search, Printer } from 'lucide-react';
import { toast } from 'sonner';
import PracticeScenario from '../shared/PracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function BodySensations() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  const sensations = [
    // Column 1
    'Achy', 'Airy', 'Antsy', 'Bloated', 'Blocked', 'Buoyant', 'Breathless', 'Bubbly', 'Burning', 'Buzzy',
    'Chilly', 'Cold', 'Cool', 'Congested', 'Constricted', 'Clammy', 'Clenched', 'Damp', 'Dense', 'Dizzy',
    'Drained', 'Dull', 'Effervescent', 'Electric', 'Empty', 'Energized', 'Expansive', 'Faint', 'Flaccid', 'Flowing',
    'Fluid', 'Flushed', 'Fluttery', 'Frantic', 'Frozen',
    // Column 2
    'Full', 'Fuzzy', 'Goose bumpy', 'Hollow', 'Hot', 'Heavy', 'Icy', 'Itchy', 'Jittery', 'Jumbled',
    'Jumpy', 'Lax', 'Lethargic', 'Light', 'Light-headed', 'Limp', 'Loose', 'Lumpy', 'Mild', 'Nauseous',
    'Numb', 'Open', 'Paralyzed', 'Pounding', 'Pressure', 'Prickly', 'Puffy', 'Pulsing', 'Queasy', 'Quivery',
    'Radiant', 'Relaxed', 'Scrunchy', 'Shaky', 'Sharp',
    // Column 3
    'Shivery', 'Shuddery', 'Sneezy', 'Sore', 'Spazzy', 'Spinning', 'Squirrelly', 'Squirmy', 'Still',
    '(like I\'m) Suffocating', 'Sweaty', 'Teetery', 'Tender', 'Tense', 'Thick', 'Throbbing', 'Tight',
    'Tightness of skin', 'Tingly', 'Trembly', 'Tremulous', 'Twisted', 'Twitchy', 'Unstable', 'Warm', 'Weak', 'Wobbly'
  ];

  const filteredSensations = sensations.filter(sensation =>
    sensation.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">Body Sensations</h1>
                <p className="text-slate-300 text-sm">Physical sensations we experience</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Page 7</span>
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search sensations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400 focus:border-slate-500"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Intro Text Box */}
          <div className="bg-gradient-to-br from-teal-900/30 to-emerald-900/20 border border-teal-600/30 rounded-xl p-8 mb-10">
            <p className="text-teal-50 text-lg leading-relaxed mb-4">
              In the NVC model of communication, we often use the word "feelings" to describe the
              felt sense of what is happening internally. Feelings also give us important information
              about whether our needs have been met or not. This list of body sensations can be
              used in the same way.
            </p>
            <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-teal-500">
              <p className="text-slate-200 italic">
                For example, <span className="text-teal-300">"When you walked out of the room while I was talking, I felt <strong>frozen</strong> and <strong>weak</strong>."</span>
              </p>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-4">
            <p className="text-slate-400 text-sm">
              Showing {filteredSensations.length} of {sensations.length} sensations
            </p>
          </div>

          {/* Sensations Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filteredSensations.map((sensation, idx) => (
              <div
                key={idx}
                className="bg-slate-800/40 border border-slate-700/50 rounded-lg px-4 py-3 hover:border-slate-600/50 hover:bg-slate-800/60 transition-colors"
                data-testid={`sensation-${idx}`}
              >
                <span className="text-slate-100 text-sm">{sensation}</span>
              </div>
            ))}
          </div>

          {/* No Results */}
          {filteredSensations.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-400">No sensations found matching "{searchTerm}"</p>
            </div>
          )}
        </div>
      </div>

      {/* Practice Scenario */}
      <PracticeScenario pageTitle="Body Sensations" pageNumber={7} />

      {/* Page Messaging */}
      <PageMessaging pageTitle="Body Sensations" pageNumber={7} />
    </div>
  );
}
